/**
 * Quick date-range presets for sectioned filter panels.
 *
 * The reworked filter panels put a row of period chips ("Today", "This week",
 * …) above the From/To inputs. The chips are pure sugar over those two fields —
 * picking one writes a concrete From and To, so the backend query is identical
 * to typing the dates by hand and nothing downstream needs to know a chip was
 * used.
 *
 * `formatLocalDate` existed as two separate copies (dashboard filter utils and
 * the delivery filter section). Both format a Date for a `<input type="date">`
 * and for the API's `date_from` / `date_to`. It lives here now so panels adding
 * chips don't spawn a third copy.
 */

/**
 * Format a Date as `YYYY-MM-DD` in the **viewer's** timezone.
 *
 * Deliberately not `toISOString().split('T')[0]`: that converts to UTC first,
 * so any local time before midnight UTC — the whole BST evening, for instance —
 * reports the previous day and silently shifts the range by one.
 *
 * @param d - the date to format
 * @returns the local calendar date as `YYYY-MM-DD`
 */
export function formatLocalDate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

export interface DateRangePreset {
  /** Stable identifier, used as the React key and the active-chip test. */
  key: string;
  /** Chip caption. */
  label: string;
  /** Resolve the preset to a concrete range, relative to `now`. */
  resolve: (now: Date) => { from: string; to: string };
}

/**
 * The five presets shown in the filter designs, in display order.
 *
 * `now` is passed in rather than read from the clock so callers can resolve a
 * range and test the result deterministically.
 */
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    key: 'today',
    label: 'Today',
    resolve: (now) => ({ from: formatLocalDate(now), to: formatLocalDate(now) }),
  },
  {
    key: 'week',
    label: 'This week',
    resolve: (now) => {
      // ISO weeks run Monday–Sunday; getDay() is Sunday-based, so Sunday (0)
      // belongs to the week that started six days earlier, not the one starting
      // tomorrow.
      const dayOfWeek = now.getDay();
      const daysSinceMonday = (dayOfWeek + 6) % 7;
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday);
      const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
      return { from: formatLocalDate(start), to: formatLocalDate(end) };
    },
  },
  {
    key: 'month',
    label: 'This month',
    resolve: (now) => {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      // Day 0 of the next month is the last day of this one, which keeps this
      // correct for February and leap years without a length table.
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: formatLocalDate(start), to: formatLocalDate(end) };
    },
  },
  {
    key: 'quarter',
    label: 'This quarter',
    resolve: (now) => {
      const firstMonthOfQuarter = Math.floor(now.getMonth() / 3) * 3;
      const start = new Date(now.getFullYear(), firstMonthOfQuarter, 1);
      const end = new Date(now.getFullYear(), firstMonthOfQuarter + 3, 0);
      return { from: formatLocalDate(start), to: formatLocalDate(end) };
    },
  },
  {
    key: 'year',
    label: 'This year',
    resolve: (now) => ({
      from: formatLocalDate(new Date(now.getFullYear(), 0, 1)),
      to: formatLocalDate(new Date(now.getFullYear(), 11, 31)),
    }),
  },
];

/**
 * Identify which preset, if any, produced the currently applied range.
 *
 * Used to highlight the active chip. Returns null when the dates were typed by
 * hand or only one end is set — an arbitrary range must not light up a chip.
 *
 * @param from - applied start date, `YYYY-MM-DD`
 * @param to - applied end date, `YYYY-MM-DD`
 * @param now - reference date the presets resolve against
 * @returns the matching preset key, or null
 */
export function matchDateRangePreset(from: string, to: string, now: Date): string | null {
  if (!from || !to) return null;
  const hit = DATE_RANGE_PRESETS.find((preset) => {
    const range = preset.resolve(now);
    return range.from === from && range.to === to;
  });
  return hit ? hit.key : null;
}
